import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '../../../lib/analytics';
import { useMixpanel } from '../../../lib/mixpanel';
import { linkedinSearchAPI } from '../api/linkedinSearchApi';
import type { CreateSegmentInput, ExecuteSegmentInput, UpdateSegmentInput } from '../types';

const segmentKeys = {
  all: ['segments'] as const,
  lists: () => [...segmentKeys.all, 'list'] as const,
  list: (orgId: string | undefined) => [...segmentKeys.lists(), orgId] as const,
  details: () => [...segmentKeys.all, 'detail'] as const,
  detail: (id: string | undefined) => [...segmentKeys.details(), id] as const,
  results: (id: string | undefined) => [...segmentKeys.all, 'results', id] as const,
};

export function useSegments(organizationId: string | undefined) {
  return useQuery({
    queryKey: segmentKeys.list(organizationId),
    queryFn: () => linkedinSearchAPI.listSegments(organizationId!).then((r) => r.data),
    enabled: !!organizationId,
    staleTime: 30_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.some((s) => s.status === 'running') ? 3000 : false;
    },
  });
}

export function useCreateSegment() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: (data: CreateSegmentInput) =>
      linkedinSearchAPI.createSegment(data).then((r) => r.data),
    onSuccess: () => {
      trackEvent(mp, 'segment.created');
      queryClient.invalidateQueries({ queryKey: segmentKeys.lists() });
    },
  });
}

export function useUpdateSegment() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: ({
      id,
      organizationId,
      data,
    }: {
      id: string;
      organizationId: string;
      data: UpdateSegmentInput;
    }) => linkedinSearchAPI.updateSegment(id, organizationId, data).then((r) => r.data),
    onSuccess: (_, variables) => {
      trackEvent(mp, 'segment.updated');
      queryClient.invalidateQueries({ queryKey: segmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: segmentKeys.detail(variables.id) });
    },
  });
}

export function useDeleteSegment() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: ({ id, organizationId }: { id: string; organizationId: string }) =>
      linkedinSearchAPI.deleteSegment(id, organizationId),
    onSuccess: () => {
      trackEvent(mp, 'segment.deleted');
      queryClient.invalidateQueries({ queryKey: segmentKeys.lists() });
    },
  });
}

export function useExecuteSegment() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: ({
      id,
      organizationId,
      data,
    }: {
      id: string;
      organizationId: string;
      data?: ExecuteSegmentInput;
    }) => linkedinSearchAPI.executeSegment(id, organizationId, data).then((r) => r.data),
    onSuccess: (_, variables) => {
      trackEvent(mp, 'segment.executed');
      queryClient.invalidateQueries({ queryKey: segmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: segmentKeys.results(variables.id) });
    },
  });
}
export function useSegment(id: string | undefined, organizationId: string | undefined) {
  return useQuery({
    queryKey: segmentKeys.detail(id),
    queryFn: () => linkedinSearchAPI.getSegment(id!, organizationId!).then((r) => r.data),
    enabled: !!id && !!organizationId,
    staleTime: 30_000,
  });
}

export function useSegmentResults(
  id: string | undefined,
  organizationId: string | undefined,
  limit = 50,
  offset = 0,
) {
  return useQuery({
    queryKey: [...segmentKeys.results(id), limit, offset],
    queryFn: () =>
      linkedinSearchAPI.getSegmentResults(id!, organizationId!, limit, offset).then((r) => r.data),
    enabled: !!id && !!organizationId,
    staleTime: 5 * 60_000,
  });
}
